import { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { usePageTracking } from '@/lib/usePageTracking';
import QuickCreateMenu from '@/components/layout/QuickCreateMenu';
import { motion } from 'framer-motion';
import {
  Grid3X3, Settings, LogOut, ChevronDown, Menu, Shield, Bot,
  User, Bell, Globe, Building2, KeyRound,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { GlobalSearch } from '@/components/GlobalSearch';
import { NotificationsPanel } from '@/components/NotificationsPanel';
import { AppLauncher } from '@/components/AppLauncher';
import AgentChatPanel from '@/components/AgentChatPanel';
import BriefMeButton from '@/components/BriefMeButton';
import AgentLauncher from '@/components/agents/AgentLauncher';
import IntegrationHealthIndicator from '@/components/layout/IntegrationHealthIndicator';
import TrialBanner from '@/components/layout/TrialBanner';
import GroupNavBar from '@/components/layout/GroupNavBar';
import ModuleAccessGuard from '@/components/layout/ModuleAccessGuard';
import { getCurrentModule } from '@/config/navigation';
import { SourcingRailedLayout, RailedLayout } from '@/components/layout/SourcingRailedLayout';
import { getModuleLinks } from '@/config/moduleLinks';
import GuidedDemoTour from '@/components/tour/GuidedDemoTour';
import {
  AuthoringMapProvider, AuthoringMapToggle, AuthoringMapLegend,
  PageAuthoringBanner,
} from '@/components/contracts/AuthoringMap';

/** Path → moduleKey for Sourcing pages — iter-146.
 * Brings the same "Where Authoring Happens" + Quick Links rail to the
 * sourcing module that the CM/Procurement/SRM modules already have.
 * Sourcing has pre-existing inline rails on most pages (SourcingL2Kit),
 * so only the pages WITHOUT one need the moduleKey injection. */
const SOURCING_MODULE_KEY = {
  '/app/sourcing/lifecycle':           'sourcing-strategic',
  '/app/sourcing/wizard':              'sourcing-events',
  '/app/sourcing/cockpit':             'sourcing-cockpit',
  '/app/sourcing/events':              'sourcing-events',
  '/app/sourcing/event-detail':        'sourcing-events',
  '/app/sourcing/calendar':            'sourcing-cockpit',
  '/app/sourcing/supplier-invitation': 'supplier-invitation',
  '/app/sourcing/clarifications':      'clarifications',
  '/app/sourcing/clarification-hub':   'clarifications',
  '/app/sourcing/bid-submission':      'bid-submission',
  '/app/sourcing/evaluation':          'evaluation',
  '/app/sourcing/auction':             'auction',
  '/app/sourcing/bafo':                'bafo',
  '/app/sourcing/framework-agreements': 'framework',
  '/app/sourcing/direct-award':        'direct-award',
  '/app/sourcing/award-recommendation': 'award-recommendation',
  '/app/sourcing/communication-engine': 'communication-engine',
  '/app/sourcing/analytics':           'sourcing-analytics',
  '/app/sourcing/portal-admin':        'supplier-portal-admin',
  '/app/sourcing/itt-compliance':      'itt-compliance',
};

/** Path → moduleKey (for the canonical Quick Links registry) for non-sourcing module groups. */
const SCM_SRM_MODULE_KEY = {
  '/app/scm/taxonomy': 'taxonomy',
  '/app/scm/spend-cube': 'spend-cube',
  '/app/scm/kraljic': 'kraljic',
  '/app/scm/strategy': 'strategy',
  '/app/scm/market-intel': 'market-intel',
  '/app/scm/demand-planning': 'demand-planning',
  '/app/srm/registration': 'registration',
  '/app/srm/qualification': 'qualification',
  '/app/srm/performance': 'performance',
  '/app/srm/relationship': 'relationship',
  '/app/srm/risk': 'risk',
  '/app/srm/data-governance': 'data-governance',
  '/app/srm/diversity': 'diversity',
  '/app/srm/collaboration': 'collaboration',
};

/**
 * Path → moduleKey for Contracts Lifecycle Management sub-modules.
 * Iter-145: aligns Contracts with Strategic Sourcing — each cm/* page
 * gets its own Quick Links + Authoring info in the left rail.
 */
const CM_MODULE_KEY = {
  '/app/cm/repository': 'cm-repository',
  '/app/cm/portfolio': 'cm-portfolio',
  '/app/cm/bonds': 'cm-bonds',
  '/app/cm/insurance': 'cm-insurance',
  '/app/cm/contract-types': 'cm-contract-types',
  '/app/cm/integrations': 'cm-integrations',
  '/app/cm/request-form': 'cm-request-form',
  '/app/cm/risk-assessment': 'cm-risk-assessment',
  '/app/cm/clause-library': 'cm-clause-library',
  '/app/cm/clause-architecture': 'cm-clause-library',
  '/app/cm/templates': 'cm-templates',
  '/app/cm/ai-drafting': 'cm-ai-drafting',
  '/app/cm/ai-generation': 'cm-ai-generation',
  '/app/cm/collaborative-editing': 'cm-collaborative-editing',
  '/app/cm/self-review': 'cm-self-review',
  '/app/cm/internal-review': 'cm-internal-review',
  '/app/cm/multi-stakeholder': 'cm-multi-stakeholder',
  '/app/cm/legal-review': 'cm-legal-review',
  '/app/cm/negotiation': 'cm-negotiation',
  '/app/cm/external-negotiation': 'cm-external-negotiation',
  '/app/cm/redline-exchange': 'cm-redline-exchange',
  '/app/cm/playbooks': 'cm-playbooks',
  '/app/cm/esign': 'cm-esign',
  '/app/cm/approval-signing': 'cm-approval-signing',
  '/app/cm/ai-obligation': 'cm-ai-obligation',
  '/app/cm/obligation-categories': 'cm-obligation-categories',
  '/app/cm/obligation-tracking': 'cm-obligation-tracking',
  '/app/cm/obligations': 'cm-obligations',
  '/app/cm/kpi-framework': 'cm-kpi-framework',
  '/app/cm/financial-monitoring': 'cm-financial-monitoring',
  '/app/cm/health-score': 'cm-health-score',
  '/app/cm/health-score-v2': 'cm-health-score',
  '/app/cm/performance': 'cm-performance',
  '/app/cm/amendment-types': 'cm-amendment-types',
  '/app/cm/amendment-workflow': 'cm-amendment-workflow',
  '/app/cm/change-order-wizard': 'cm-change-order-wizard',
  '/app/cm/cost-impact': 'cm-cost-impact',
  '/app/cm/variation-log': 'cm-variation-log',
  '/app/cm/amendments': 'cm-amendments',
  '/app/cm/expiry-alerts': 'cm-expiry-alerts',
  '/app/cm/renewal-intelligence': 'cm-renewal-intelligence',
  '/app/cm/contract-closure': 'cm-contract-closure',
  '/app/cm/expiry': 'cm-contract-closure',
  '/app/cm/renewal': 'cm-renewal',
  '/app/cm/draft': 'cm-ai-drafting',
  '/app/cm/standard-reports': 'cm-performance',
};

/** Path → moduleKey for Procurement (PR/PO/GRN/Invoices/Catalogue) sub-modules. */
const PROCUREMENT_MODULE_KEY = {
  '/app/p2p': 'requisitions',
  '/app/requisitions': 'requisitions',
  '/app/pr/creation': 'requisitions',
  '/app/pr/nlp-intake': 'requisitions',
  '/app/pr/ai-enrichment': 'requisitions',
  '/app/pr/compliance': 'requisitions',
  '/app/pr/approval-workflow': 'requisitions',
  '/app/pr/approval-matrix': 'requisitions',
  '/app/pr/approver': 'requisitions',
  '/app/po': 'po',
  '/app/po/types': 'po',
  '/app/po/automation': 'po',
  '/app/po/terms': 'po',
  '/app/po/communication': 'po',
  '/app/po/amendments': 'po',
  '/app/po/tracking': 'po',
  '/app/po/analytics': 'po',
  '/app/po/copilot': 'po',
  '/app/purchase-orders': 'po',
  '/app/grn': 'grn',
  '/app/grn/asn': 'grn',
  '/app/grn/receipt': 'grn',
  '/app/grn/inspection': 'grn',
  '/app/grn/returns': 'grn',
  '/app/grn/ses': 'grn',
  '/app/grn/analytics': 'grn',
  '/app/goods-receipt': 'grn',
  '/app/invoices': 'invoice',
  '/app/invoices/admin': 'invoice',
  '/app/invoices/mobile': 'invoice',
  '/app/invoices/legacy': 'invoice',
  '/app/invoices/exceptions': 'invoice',
  '/app/invoices/payments': 'invoice',
  '/app/catalogue': 'catalogue',
  '/app/catalogue/internal': 'catalogue',
  '/app/catalogue/punchout': 'catalogue',
  '/app/claims': 'claims',
  '/app/cld/register': 'claims',
  '/app/cld/manual-initiation': 'claims',
  '/app/cld/ld-config': 'claims',
  '/app/variations': 'change-orders',
};

/** Iter-162 — Path → moduleKey for Claims & LD sub-modules. */
const CLD_MODULE_KEY = {
  '/app/cld':                        'cld-register',
  '/app/cld/register':               'cld-register',
  '/app/cld/manual-initiation':      'cld-claim-initiation',
  '/app/cld/v2':                     'cld-workbench',
  '/app/cld/portfolio-analytics':    'cld-portfolio-analytics',
  '/app/cld/predictive-risk':        'cld-predictive-risk',
  '/app/cld/fm-notice':              'cld-fm-notice',
  '/app/cld/fm-notice-v2':           'cld-fm-notice',
  '/app/cld/evidence-repository':    'cld-evidence',
  '/app/cld/evidence-v2':            'cld-evidence',
  '/app/cld/quantum-engine':         'cld-quantum',
  '/app/cld/quantum-workbench':      'cld-quantum',
  '/app/cld/ld-clause-library':     'cld-ld-clause-library',
  '/app/cld/ld-config':              'cld-ld-config',
  '/app/cld/ld-notice':              'cld-ld-notice',
  '/app/cld/negotiation-table':      'cld-negotiation',
  '/app/cld/enforcement-board':      'cld-enforcement',
  '/app/cld/adjudication':           'cld-adjudication',
  '/app/cld/adjudication-list':      'cld-adjudication',
  '/app/cld/adjudication/case':      'cld-adjudication-case',
  '/app/cld/recovery-ledger':        'cld-recovery',
};

function resolveModuleKey(pathname) {
  // Combined dictionary (Sourcing + CM + Procurement + SCM/SRM).
  const ALL = { ...SOURCING_MODULE_KEY, ...SCM_SRM_MODULE_KEY,
                ...CM_MODULE_KEY, ...PROCUREMENT_MODULE_KEY,
                ...CLD_MODULE_KEY };
  // Exact match first, then longest prefix match (handles /app/scm/strategy/:id).
  if (ALL[pathname]) return ALL[pathname];
  const prefixes = Object.keys(ALL).sort((a, b) => b.length - a.length);
  for (const p of prefixes) { if (pathname.startsWith(p + '/')) return ALL[p]; }
  // CM/CLD fallback — keep the rail mounted with a generic CM panel.
  if (pathname.startsWith('/app/cm/')) return 'cm-generic';
  if (pathname.startsWith('/app/cld/')) return 'claims';
  return null;
}

function SettingsMenuItem({ icon: Icon, label, onClick, testid, danger }) {
  return (
    <button onClick={onClick} data-testid={testid}
            className={`w-full text-left px-4 py-2 text-sm flex items-center gap-3 transition-colors ${
              danger ? 'text-rose-600 hover:bg-rose-50' : 'text-gray-700 hover:bg-gray-50'
            }`}>
      <Icon className="w-4 h-4 flex-shrink-0" />
      <span dangerouslySetInnerHTML={{ __html: label }} />
    </button>
  );
}


export default function AppLayout() {
  const [launcherOpen, setLauncherOpen] = useState(false);
  const [agentOpen, setAgentOpen] = useState(false);
  const [agentInitial, setAgentInitial] = useState({ prompt: '', persona: null });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const currentModule = getCurrentModule(location.pathname);

  // Iter-170: page-view tracking for the Superadmin Portal analytics
  usePageTracking();

  // Close the settings dropdown on outside click
  useEffect(() => {
    if (!settingsOpen) return;
    const handler = (e) => {
      if (!e.target.closest('[data-settings-menu]')) setSettingsOpen(false);
    };
    window.addEventListener('mousedown', handler);
    return () => window.removeEventListener('mousedown', handler);
  }, [settingsOpen]);

  // Global window event that any component (e.g. CognaraSuggestionChip)
  // can fire to open the agent panel with a preset persona + prompt.
  useEffect(() => {
    const handler = (e) => {
      const { prompt = '', persona } = e.detail || {};
      setAgentInitial({ prompt, persona });
      setAgentOpen(true);
    };
    window.addEventListener('cognara:open', handler);
    return () => window.removeEventListener('cognara:open', handler);
  }, []);

  const handleLogout = async () => { await logout(); navigate('/'); };

  return (
    <AuthoringMapProvider>
    <div className="flex flex-col h-screen bg-gray-50 overflow-hidden">
      <ModuleAccessGuard />
      {/* Header */}
      <header className="h-14 border-b border-gray-200 bg-white flex items-center px-4 gap-3 shrink-0 z-30">
        {/* Logo */}
        <div className="cursor-pointer shrink-0 flex items-center" onClick={() => navigate('/app')} data-testid="header-logo">
          <img src="/procurex-logo-light.svg" alt="ProcureX" className="h-8" />
        </div>

        {/* Divider */}
        <div className="hidden md:block w-px h-6 bg-gray-200" />

        {/* Apps Launcher Button */}
        <Button
          variant="outline"
          size="sm"
          className={`h-8 gap-2 text-xs font-medium transition-all duration-200 shrink-0 ${
            launcherOpen
              ? 'bg-[#1E4E8C] text-white border-[#1E4E8C] hover:bg-[#163D70] hover:text-white'
              : 'text-gray-600 border-gray-200 hover:border-gray-300'
          }`}
          onClick={() => setLauncherOpen(!launcherOpen)}
          data-testid="apps-launcher-btn"
        >
          <Grid3X3 className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Apps</span>
          <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${launcherOpen ? 'rotate-180' : ''}`} />
        </Button>

        {/* Current Module Badge */}
        {currentModule && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-50 border border-gray-100 shrink-0"
          >
            <div
              className="w-5 h-5 rounded-md flex items-center justify-center"
              style={{ background: `linear-gradient(135deg, ${currentModule.color}, ${currentModule.colorEnd})` }}
            >
              <currentModule.icon className="w-3 h-3 text-white" strokeWidth={2} />
            </div>
            <span className="text-xs font-medium text-gray-700">{currentModule.label}</span>
          </motion.div>
        )}

        {/* Search */}
        <div className="flex-1 flex justify-center">
          <GlobalSearch />
        </div>

        {/* Iter-163 — Brief me on this page (auto-renders when current page has a Cognara entry) */}
        <BriefMeButton/>

        {/* Right side */}
        <div className="flex items-center gap-1.5 shrink-0">
          {/* ⌘K Agent launcher hint */}
          <button
            onClick={() => window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true }))}
            className="hidden md:flex items-center gap-1.5 h-8 px-2.5 rounded-md text-xs font-medium text-violet-700 bg-violet-50 hover:bg-violet-100 border border-violet-200 transition"
            title="Launch any AI agent (⌘K)"
            data-testid="agent-launcher-btn">
            <Bot className="w-3.5 h-3.5"/>
            <span>Agents</span>
            <span className="hidden lg:inline text-[10px] font-mono bg-white border border-violet-200 px-1 rounded">⌘K</span>
          </button>
          <NotificationsPanel />
          {/* Iter-175 — global "+ Create" menu, visible on every page */}
          <QuickCreateMenu />
          {(user?.role === 'superadmin' || user?.role === 'admin') && (
            <IntegrationHealthIndicator />
          )}
          {/* Iter-147 — global Authoring Map toggle, visible on every page */}
          <div className="hidden md:block">
            <AuthoringMapToggle />
          </div>
          {user?.role === 'superadmin' && (
            <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-steel-600"
              onClick={() => navigate('/app/admin')} data-testid="admin-panel-btn" title="Administration">
              <Shield className="w-4 h-4" />
            </Button>
          )}
          <div className="relative" data-settings-menu>
            <Button variant="ghost" size="icon"
                    className={`h-8 w-8 text-gray-400 hover:text-gray-600 ${settingsOpen ? 'bg-gray-100 text-gray-700' : ''}`}
                    data-testid="settings-btn"
                    title="Settings"
                    onClick={() => setSettingsOpen(o => !o)}>
              <Settings className="w-4 h-4" />
            </Button>
            {settingsOpen && (
              <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.12 }}
                          className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-xl z-50 overflow-hidden"
                          data-testid="settings-dropdown">
                <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/50">
                  <div className="text-[10px] font-mono uppercase tracking-wider text-gray-400 mb-1">Signed in as</div>
                  <div className="text-sm font-medium text-gray-900 truncate">{user?.name || user?.email}</div>
                  <div className="text-xs text-gray-500 truncate">{user?.email}</div>
                </div>
                <div className="py-1">
                  <SettingsMenuItem icon={User} label="My profile"
                                    onClick={() => { setSettingsOpen(false); navigate('/app/profile'); }}
                                    testid="settings-profile" />
                  <SettingsMenuItem icon={Bell} label="Notification preferences"
                                    onClick={() => { setSettingsOpen(false); navigate('/app/notifications'); }}
                                    testid="settings-notifications" />
                  <SettingsMenuItem icon={KeyRound} label="Change password"
                                    onClick={() => { setSettingsOpen(false); navigate('/app/profile?tab=security'); }}
                                    testid="settings-password" />
                  <SettingsMenuItem icon={Globe} label="Language &amp; region"
                                    onClick={() => { setSettingsOpen(false); navigate('/app/profile?tab=preferences'); }}
                                    testid="settings-language" />
                  {user?.role === 'superadmin' && (
                    <>
                      <div className="my-1 h-px bg-gray-100" />
                      <SettingsMenuItem icon={Building2} label="Organization settings"
                                        onClick={() => { setSettingsOpen(false); navigate('/app/admin'); }}
                                        testid="settings-org" />
                      <SettingsMenuItem icon={Shield} label="Users &amp; roles"
                                        onClick={() => { setSettingsOpen(false); navigate('/app/admin?tab=users'); }}
                                        testid="settings-rbac" />
                      <SettingsMenuItem icon={Shield} label="Superadmin portal"
                                        onClick={() => { setSettingsOpen(false); navigate('/superadmin'); }}
                                        testid="settings-superadmin-portal" />
                    </>
                  )}
                </div>
                <div className="border-t border-gray-100 py-1">
                  <SettingsMenuItem icon={LogOut} label="Sign out" danger
                                    onClick={() => { setSettingsOpen(false); logout(); navigate('/login'); }}
                                    testid="settings-logout" />
                </div>
              </motion.div>
            )}
          </div>
          <div className="w-px h-6 bg-gray-200 mx-1 hidden md:block" />
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#1E4E8C]/10 flex items-center justify-center">
              <span className="text-xs font-bold text-[#1E4E8C]">{user?.name?.[0] || 'U'}</span>
            </div>
            <div className="hidden md:block">
              <p className="text-xs font-medium text-gray-900 leading-none">{user?.name || 'User'}</p>
              <p className="text-[9px] text-gray-400">{user?.role || 'PM'}</p>
            </div>
            <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-400 hover:text-gray-600" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Trial countdown banner (renders only for is_trial=true users) */}
      <TrialBanner />

      {/* v3.0 Context-Aware Group Navigation */}
      <GroupNavBar />

      {/* Sub-module Navigation Bar */}
      {currentModule?.children && (
        <div className="border-b border-gray-100 bg-white/80 backdrop-blur-sm flex items-center px-4 py-1.5 gap-1 shrink-0" data-testid="subnav-bar">
          <div
            className="w-4 h-4 rounded flex items-center justify-center shrink-0 mr-1"
            style={{ background: `linear-gradient(135deg, ${currentModule.color}, ${currentModule.colorEnd})` }}
          >
            <currentModule.icon className="w-2.5 h-2.5 text-white" strokeWidth={2} />
          </div>
          <div className="flex items-center gap-0.5 flex-wrap">
            {(() => {
              const urlParams = new URLSearchParams(location.search);
              const noUrlParams = [...urlParams.keys()].length === 0;
              return currentModule.children.map((child, idx) => {
                const [childPath, childQuery] = child.path.split('?');
                const childParams = new URLSearchParams(childQuery || '');
                const childKeys = [...childParams.keys()];
                const pathMatches = location.pathname === childPath;
                const allParamsMatch = [...childParams.entries()]
                  .every(([k, v]) => urlParams.get(k) === v);
                // Strict match: the URL must declare exactly the same params as the child.
                const sameParamCount = childKeys.length === [...urlParams.keys()].length;
                let isActive = pathMatches && allParamsMatch && sameParamCount;
                // Fallback: when URL has zero params, highlight the very first child
                // (canonical entrypoint, e.g. PO Workbench, RQ-5.1 Dashboard).
                if (!isActive && pathMatches && noUrlParams && idx === 0) isActive = true;
                const testKey = childParams.get('sub') || childParams.get('tab') || childPath.split('/').pop();
                return (
                  <NavLink key={child.path} to={child.path}
                    className={`px-2 py-1 rounded-full text-[11px] whitespace-nowrap transition-all duration-200 ${
                      isActive
                        ? 'bg-[#1E4E8C]/10 text-[#1E4E8C] font-medium'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    }`}
                    data-testid={`subnav-${testKey}`}
                  >
                    {child.label}
                  </NavLink>
                );
              });
            })()}
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 overflow-auto min-h-0 bg-gray-50" data-testid="app-main">
        {(() => {
          const mk = resolveModuleKey(location.pathname);
          const moduleAuthoring = mk ? getModuleLinks(mk)?.authoring : null;
          // Render a tiny inline marker above the main content so EVERY page
          // gets a "Where Authoring Happens" hint when the toggle is on.
          const banner = (
            <div className="px-3 pt-3">
              <PageAuthoringBanner moduleKey={mk} authoring={moduleAuthoring}/>
            </div>
          );
          if (location.pathname.startsWith('/app/sourcing/')) {
            return (
              <SourcingRailedLayout moduleKey={mk}>
                {banner}
                <Outlet />
              </SourcingRailedLayout>
            );
          }
          const wrapInRailed = (
            location.pathname.startsWith('/app/srm/') ||
            location.pathname.startsWith('/app/scm/') ||
            location.pathname.startsWith('/app/cm/') ||
            location.pathname.startsWith('/app/cld/') ||
            location.pathname.startsWith('/app/p2p') ||
            location.pathname.startsWith('/app/pr/') ||
            location.pathname.startsWith('/app/po') ||
            location.pathname.startsWith('/app/purchase-orders') ||
            location.pathname.startsWith('/app/grn') ||
            location.pathname.startsWith('/app/goods-receipt') ||
            location.pathname.startsWith('/app/invoices') ||
            location.pathname.startsWith('/app/catalogue') ||
            location.pathname.startsWith('/app/requisitions') ||
            location.pathname.startsWith('/app/claims') ||
            location.pathname.startsWith('/app/variations'));
          if (wrapInRailed) {
            return (
              <RailedLayout moduleKey={mk}>
                {banner}
                <Outlet />
              </RailedLayout>
            );
          }
          return <><>{banner}<Outlet /></></>;
        })()}
      </main>

      {/* App Launcher Overlay */}
      <AppLauncher open={launcherOpen} onClose={() => setLauncherOpen(false)} />

      {/* ⌘K — Global AI Agent Launcher (39 agents) */}
      <AgentLauncher />

      {/* iter-227 — 5-step Guided Demo Tour overlay */}
      <GuidedDemoTour />

      {/* Agentic AI floating button — bottom-right */}
      <button
        onClick={() => setAgentOpen(true)}
        className={`fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-gradient-to-br from-sky-500 to-indigo-600 text-white shadow-xl hover:scale-105 transition-transform flex items-center justify-center ${agentOpen ? 'hidden' : ''}`}
        data-testid="agent-fab"
        title="Ask Agentic AI">
        <Bot className="w-6 h-6"/>
      </button>
      <AgentChatPanel
        open={agentOpen}
        onClose={() => { setAgentOpen(false); setAgentInitial({ prompt: '', persona: null }); }}
        initialPrompt={agentInitial.prompt}
        initialPersona={agentInitial.persona}
      />
    </div>
    </AuthoringMapProvider>
  );
}
